import { useCallback, useMemo, useState } from 'react';

import {
  useZoomAssignedGroupsQuery,
  useZoomUnassignedGroupsQuery,
} from 'core/graphql';

/** Minimal group shape used by the Zoom accounts panel (spec 0093: «Интеграции»). */
export interface IAccountGroup {
  id: string;
  name: string;
  startDate?: string | null;
}

interface IAccountGroupsHook {
  /** Bound groups grouped by their `zoomAccount.id`. */
  groupsByAccountId: Record<string, IAccountGroup[]>;
  /** Groups with no Zoom account bound (filtered by the search term). */
  unassignedGroups: IAccountGroup[];
  setUnassignedSearch: (search: string) => void;
  loading: boolean;
  error?: string;
  refetch: () => void;
}

/**
 * Feeds the Zoom accounts panel («Настройки → Интеграции → Zoom», until spec
 * 0093 — `/zoom/accounts`): one query for every group that already has
 * a Zoom account (grouped here by account id), one searchable query for the
 * groups still unbound. See docs/zoom_accounts_groups_tz.md for the backend.
 */
export const useAccountGroups = (): IAccountGroupsHook => {
  const [unassignedSearch, setUnassignedSearch] = useState('');

  const {
    data: assignedData,
    loading: assignedLoading,
    error: assignedError,
    refetch: refetchAssigned,
  } = useZoomAssignedGroupsQuery({
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: unassignedData,
    loading: unassignedLoading,
    error: unassignedError,
    refetch: refetchUnassigned,
  } = useZoomUnassignedGroupsQuery({
    variables: { query: unassignedSearch || null, first: 50 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const groupsByAccountId = useMemo(() => {
    const map: Record<string, IAccountGroup[]> = {};
    (assignedData?.allGroups?.edges ?? []).forEach((edge) => {
      const node = edge?.node;
      const accountId = node?.zoomAccount?.id;
      if (!node || !accountId) {
        return;
      }
      (map[accountId] = map[accountId] ?? []).push({
        id: node.id,
        name: node.name,
        startDate: node.startDate,
      });
    });
    return map;
  }, [assignedData]);

  const unassignedGroups = useMemo(
    () =>
      (unassignedData?.allGroups?.edges ?? [])
        .map((edge) => edge?.node)
        .filter((node): node is NonNullable<typeof node> => Boolean(node))
        .map((node) => ({
          id: node.id,
          name: node.name,
          startDate: node.startDate,
        })),
    [unassignedData],
  );

  const refetch = useCallback(() => {
    refetchAssigned();
    refetchUnassigned();
  }, [refetchAssigned, refetchUnassigned]);

  return {
    groupsByAccountId,
    unassignedGroups,
    setUnassignedSearch,
    loading: assignedLoading || unassignedLoading,
    error: assignedError?.message ?? unassignedError?.message,
    refetch,
  };
};
